import type { PropertyNode } from '../storedValue.ts';
import { readStoredValue } from '../storedValue.ts';

/** Read and written by the editor. Confluence copies it to the published key on publish. */
export const NUMBERED_HEADINGS_DRAFT_PROPERTY_KEY = 'numbered-headings-draft';

/** Read by the renderer on the published page. */
export const NUMBERED_HEADINGS_PUBLISHED_PROPERTY_KEY = 'numbered-headings-published';

/** The stored setting: whether numbering is on, and the property version (0 when not stored). */
export type NumberedHeadingsSetting = {
	enabled: boolean | undefined;
	version: number;
	isStored: boolean;
};

const parseBoolean = (value: unknown): boolean | undefined => {
	if (typeof value === 'boolean') {
		return value;
	}
	if (value === 'true' || value === 'false') {
		return value === 'true';
	}
	return undefined;
};

/**
 * Values are saved as "true" or "false"; `{ "enabled": true }` and bare booleans are read too, so
 * older values keep working.
 */
export const parseNumberedHeadingsEnabled = (value: unknown): boolean | undefined =>
	parseBoolean(
		typeof value === 'object' && value !== null && 'enabled' in value ? value.enabled : value,
	);

/** The setting stored in a numbered headings content property. */
export const getNumberedHeadingsSetting = (
	nodes: readonly PropertyNode[] | null | undefined,
): NumberedHeadingsSetting => {
	const { value, version, isStored } = readStoredValue(nodes, parseNumberedHeadingsEnabled);
	return { enabled: value, version, isStored };
};

/** The property value to save. useUpdateContentProperty encodes it as JSON. */
export const serializeNumberedHeadingsEnabled = (enabled: boolean): string => String(enabled);
